import { useContext, useEffect, useMemo, useState } from 'react';

import { TabContext, TabPanel } from '@mui/lab';
import { Box, Button, Fade, Stack, Typography } from '@mui/material';
import { createFileRoute } from '@tanstack/react-router';

import ControlWithLabel from '../../components/ControlWithLabel';
import EmptyStateCard from '../../components/EmptyStateCard';
import PageFooter from '../../components/PageFooter';
import RequestLabel from '../../components/RequestLabel';
import { StyledTab, StyledTabs } from '../../components/StyledTabs';
import TopLevelPageHeader from '../../components/TopLevelPageHeader';
import { useSnackbar } from '../../contexts/SnackbarContext';
import useResponsive from '../../hooks/useResponsive';
import { useTeamMemberData } from '../../hooks/useTeamMemberData';
import MainNav from 'src/components/MainNav';
import { AnalyticsContext } from 'src/contexts/AnalyticsContext';
import { InnerMaxWidthContainer } from 'src/layouts/InnerMaxWidthContainer';
import {
    RequestStatus,
    useGetRequestsQuery,
    useUpdateRequestMutation,
} from 'src/store/sageHomeApi';

export const Route = createFileRoute('/(app)/requests')({
    component: SupplyRequestComponent,
});

const PageName = 'Requests Page';

export const RequestStatuses = {
    NEEDED: 'NEEDED' as RequestStatus,
    COMPLETED: 'COMPLETED' as RequestStatus,
} as const;

function SupplyRequestComponent() {
    const [requestFilter, setRequestFilter] = useState<'needed' | 'completed'>('needed');
    const [selectedRequestIds, setSelectedRequestIds] = useState<Set<string>>(new Set());
    const { isMobile } = useResponsive();
    const { showSuccess, showError } = useSnackbar();
    const { trackPageView, trackEvent } = useContext(AnalyticsContext);
    const { careRecipient, currentUser } = useTeamMemberData();

    const { data: requests = [], isLoading: isLoadingRequests } = useGetRequestsQuery(
        {
            careRecipientRid: careRecipient?.teamMemberRid ?? '',
            lastNDays: 30,
        },
        {
            skip: !careRecipient?.teamMemberRid,
        }
    );
    const [updateRequest, { isLoading: isUpdatingRequest }] = useUpdateRequestMutation();

    const neededRequests = useMemo(
        () => requests.filter((request) => request.status === RequestStatuses.NEEDED),
        [requests]
    );
    const completedRequests = useMemo(
        () => requests.filter((request) => request.status === RequestStatuses.COMPLETED),
        [requests]
    );

    const selectedCount = useMemo(
        () =>
            neededRequests.filter(
                (request) => request.requestRid && selectedRequestIds.has(request.requestRid)
            ).length,
        [neededRequests, selectedRequestIds]
    );

    const handleRequestChange = (id: string, checked: boolean) => {
        setSelectedRequestIds((selectedRequest) => {
            const newSet = new Set(selectedRequest);
            if (checked) {
                newSet.add(id);
            } else {
                newSet.delete(id);
            }
            return newSet;
        });
    };

    const isMarkCompleteButtonDisabled = selectedCount === 0 || isUpdatingRequest;

    const handleMarkAsComplete = async () => {
        if (!careRecipient?.teamMemberRid) {
            showError('Missing required information');
            return;
        }

        const idsToUpdate = Array.from(selectedRequestIds.values());
        if (idsToUpdate.length === 0) {
            return;
        }

        trackEvent('Mark Requests Complete Button Clicked', { context: PageName });

        try {
            // TODO: create endpoint to fulfill multiple requests
            await Promise.all(
                idsToUpdate.map((requestRid) =>
                    updateRequest({
                        careRecipientRid: careRecipient.teamMemberRid,
                        requestRid,
                        updateRequestRequest: {
                            status: RequestStatuses.COMPLETED,
                            fulfilledByTeamMemberRid: currentUser?.teamMemberRid,
                        },
                    }).unwrap()
                )
            );
            setSelectedRequestIds(new Set());
            showSuccess('Requests marked as complete');
        } catch (error) {
            console.error('Failed to update requests', error);
            showError('Unable to mark requests as complete. Please try again.');
        }
    };

    useEffect(() => {
        trackPageView(PageName);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <InnerMaxWidthContainer>
            <Stack sx={{ gap: 2.5 }}>
                <TopLevelPageHeader pageTitle="Requests" />
                <TabContext value={requestFilter}>
                    <StyledTabs
                        value={requestFilter}
                        onChange={(_event, newValue) => setRequestFilter(newValue)}
                    >
                        <StyledTab
                            label={`Needed ${neededRequests.length > 0 ? `(${neededRequests.length})` : ''}`}
                            value="needed"
                            disableRipple
                        />
                        <StyledTab
                            label={`Completed ${completedRequests.length > 0 ? `(${completedRequests.length})` : ''}`}
                            value="completed"
                            disableRipple
                        />
                    </StyledTabs>

                    {/* Needed Requests */}
                    <TabPanel value="needed" sx={{ paddingInline: 0, padding: 0 }}>
                        <Fade in={requestFilter === 'needed'} timeout={300}>
                            <Stack
                                sx={{
                                    gap: 2.5,
                                    mt: 1,
                                    width: '100%',
                                }}
                            >
                                {isLoadingRequests ? (
                                    <Typography variant="body2" color="gray.700">
                                        Loading requests...
                                    </Typography>
                                ) : neededRequests.length === 0 ? (
                                    <EmptyStateCard
                                        text="No supply requests"
                                        variant="request"
                                        size="large"
                                        disableBorder
                                    />
                                ) : (
                                    neededRequests.map((request) => {
                                        if (!request.requestRid) return null;
                                        const requestId = request.requestRid;
                                        return (
                                            <ControlWithLabel
                                                key={requestId}
                                                label={<RequestLabel request={request} />}
                                                onChange={(event) =>
                                                    handleRequestChange(
                                                        requestId,
                                                        event.target.checked
                                                    )
                                                }
                                                checked={selectedRequestIds.has(requestId)}
                                                controlType="checkbox"
                                            />
                                        );
                                    })
                                )}
                            </Stack>
                        </Fade>
                    </TabPanel>

                    {/* Completed Requests */}
                    <TabPanel value="completed" sx={{ paddingInline: 0, padding: 0 }}>
                        <Fade in={requestFilter === 'completed'} timeout={300}>
                            <Stack sx={{ gap: 2.5, mt: 1 }}>
                                {isLoadingRequests ? (
                                    <Typography variant="body2" sx={{ color: 'gray.700' }}>
                                        Loading requests...
                                    </Typography>
                                ) : completedRequests.length === 0 ? (
                                    <EmptyStateCard
                                        text="No completed supply requests"
                                        variant="completed-request"
                                        size="large"
                                        disableBorder
                                    />
                                ) : (
                                    completedRequests.map((request) => {
                                        if (!request.requestRid) return null;
                                        return (
                                            <Box
                                                sx={{
                                                    backgroundColor: 'common.white',
                                                    border: '2px solid',
                                                    borderColor: 'gray.300',
                                                    borderRadius: 3,
                                                    p: 2,
                                                }}
                                                key={request.requestRid}
                                            >
                                                <RequestLabel request={request} />
                                            </Box>
                                        );
                                    })
                                )}
                            </Stack>
                        </Fade>
                    </TabPanel>
                </TabContext>
            </Stack>
            {requestFilter === 'needed' && neededRequests.length > 0 && selectedCount > 0 && (
                <PageFooter includeMainNav>
                    <Button
                        variant="contained"
                        color="primary"
                        fullWidth
                        disabled={isMarkCompleteButtonDisabled}
                        onClick={handleMarkAsComplete}
                    >
                        {isUpdatingRequest
                            ? 'Updating...'
                            : selectedCount > 0
                              ? `Mark as Complete (${selectedCount})`
                              : 'Mark as Complete'}
                    </Button>
                </PageFooter>
            )}
            {isMobile && <MainNav />}
        </InnerMaxWidthContainer>
    );
}
